import {Component, inject, input, InputSignal, output, OutputEmitterRef, signal, SimpleChanges, type WritableSignal} from '@angular/core';
import {UserService} from '../../../services/user.service';
import {ConfirmationService, MenuItem} from 'primeng/api';
import {CommonModule} from '@angular/common';
import {TableModule} from 'primeng/table';
import {ConfirmPopup} from 'primeng/confirmpopup';
import {User, UserStatus} from '../../../interfaces/user';
import {Order, PageControl, TableColumn} from '../../../interfaces/application';
import {finalize} from 'rxjs';
import {ButtonModule} from 'primeng/button';
import {MenuModule} from 'primeng/menu';
import { UserStatusPipe } from "../../pipes/user-status-pipe";
import { PaginatorModule, type PaginatorState } from 'primeng/paginator';
import { LottieComponent, type AnimationOptions } from 'ngx-lottie';
import { RolePipe } from "../../pipes/role-pipe";

@Component({
  selector: 'app-table-users',
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    MenuModule,
    ConfirmPopup,
    UserStatusPipe,
    PaginatorModule,
    LottieComponent,
    RolePipe
],
  providers: [
    ConfirmationService
  ],
  templateUrl: './table-users.html',
  styleUrl: './table-users.css'
})
export class TableUsers {

  public emptyLottieOptions: AnimationOptions = {
    path: 'assets/lottie/empty.json',
    loop: true,
    autoplay: true,
  };

  private readonly _confirmationService: ConfirmationService = inject(ConfirmationService);
  private readonly _userService: UserService = inject(UserService);

  public localLoading: WritableSignal<boolean> = signal<boolean>(false);
  public loading: InputSignal<boolean> = input<boolean>(false);
  public search_term: InputSignal<string> = input<string>('');

  public view: OutputEmitterRef<User> = output<User>();
  public edit: OutputEmitterRef<User> = output<User>();
  public enable: OutputEmitterRef<string> = output<string>();
  public disable: OutputEmitterRef<string> = output<string>();
  public delete: OutputEmitterRef<string> = output<string>();

  public items: User[] = [];

  public columns: TableColumn[] = [
    {
      label: 'Nome',
      slug: 'name',
      align: 'text-start',
      sortable: true,
    },
    {
      label: 'Email',
      slug: 'email',
      align: 'text-start',
      sortable: true,
    },
    {
      label: 'Telefone',
      slug: 'phone',
      align: 'text-start',
      sortable: true,
    },
    {
      label: 'Status',
      slug: 'status',
      align: 'text-start',
      sortable: false,
    },
    {
      label: 'Data de criação',
      slug: 'created_at',
      align: 'text-start',
      sortable: false,
    },
    {
      label: 'Nível de acesso',
      slug: 'role',
      align: 'text-start',
      sortable: false,
    },
    {
      label: 'Ações',
      slug: '',
      align: 'text-start',
      sortable: false,
    },
  ];

  public pageControl: PageControl = {
    page: 0,
    take: 10,
    itemCount: 0,
    pageCount: 0,
    orderField: 'created_at',
    order: Order.DESC
  }

  protected readonly order: typeof Order = Order;
  protected readonly userStatus: typeof UserStatus = UserStatus;

  public menuItems: MenuItem[] = [
    {
      label: 'Editar',
      icon: 'fa-regular fa-pen-to-square',
      action: (user: User) => { setTimeout(() => this.edit.emit(user), 500)},
      queryParams: { show: null }
    },
    {
      label: 'Detalhes do usuário',
      icon: 'fa-solid fa-circle-info',
      action: (user: User) => { setTimeout(() => this.view.emit(user), 500)},
      queryParams: { show: null }
    },
    {
      label: 'Ativar usuário',
      icon: 'fa-solid fa-user-plus',
      action: (user: User) => { setTimeout(() => this.enable.emit(user.id!), 500)},
      queryParams: { show: (user: User) => user.status === UserStatus.INACTIVE }
    },
    {
      label: 'Desativar usuário',
      icon: 'fa-solid fa-user-minus',
      action: (user: User) => { setTimeout(() => this.disable.emit(user.id!), 500)},
      queryParams: { show: (user: User) => user.status === UserStatus.ACTIVE }
    },
    {
      label: 'Excluir',
      icon: 'fa-solid fa-ban',
      action: (user: User, event: Event) => this.confirmDelete(user.id!, event),
      queryParams: { show: null }
    },
  ];

  public ngOnChanges(changes: SimpleChanges): void {
    const {
      loading,
      search_term,
    } = changes;

    if (loading?.previousValue !== null && !loading?.currentValue) {
      this.loadData();
    }
    else if (search_term?.currentValue && search_term?.currentValue !== search_term?.previousValue) {
      this.loadData();
    }
  }

  public loadData(): void {

    const filters = {
      search_term: this.search_term() || '',
    };

    this.localLoading.set(true);
    this._userService.get(this.pageControl, filters)
      .pipe(finalize(() => this.localLoading.set(false)))
      .subscribe({
        next: response => {
          this.items = response.data;

          this.pageControl.page = response.meta.current_page;
          this.pageControl.itemCount = response.meta.total;
        }
      })
  }

  public sort(slug: string): void {
    this.pageControl.page = 0;
    this.pageControl.orderField = slug;
    this.pageControl.order = this.pageControl.order === Order.ASC ? Order.DESC : Order.ASC;

    this.loadData();
  }

  public confirmDelete(id: string, event: Event): void {
    this._confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Deseja remover este usuário?',
      acceptLabel: 'Remover',
      rejectLabel: 'Cancelar',
      rejectButtonProps: {
        outlined: true,
      },
      acceptButtonProps: {
        severity: 'danger',
      },
      accept: () => {
        this.delete.emit(id)
      },
    });
  }

  public onPageChange(e: PaginatorState): void {
    this.pageControl.page = (e?.page || 0) + 1;
    this.pageControl.take = (e?.rows || 10);

    this.loadData();
  }

}
